/* The GrowDaily phone theme -- a wallpaper the app draws for itself.
 *
 * WHY IT IS DRAWN AND NOT SHIPPED
 * -------------------------------
 * A bundled wallpaper has to be shipped at some resolution, and phones range
 * from 720x1600 to 1440x3200. Whatever is chosen is wrong somewhere: stretched
 * on the big ones, wasteful on the small ones, and a few megabytes in the APK
 * either way. Drawing it means every phone gets its own exact pixels, the
 * theme costs nothing to ship, and a new palette is four hex codes rather than
 * a round of asset work.
 *
 * On Android 12 and later this reaches further than the picture: the system
 * pulls its accent colours out of the current wallpaper, so setting this also
 * recolours notifications, settings and quick settings in GrowDaily's greens.
 * That is the closest a third-party app gets to theming a whole phone without
 * going through a manufacturer's own theme store.
 *
 * WHY THE COMPOSITION IS A SEPARATE FUNCTION
 * ------------------------------------------
 * `composition()` decides WHERE everything goes and returns plain data;
 * `paint()` just draws what it is told. That split is not ceremony -- it is
 * the only way to test the one rule that matters visually:
 *
 *   a lock-screen wallpaper must keep its top clear.
 *
 * The clock, the date and the notifications live up there. A composition with
 * its busiest area behind the clock does not look like a style choice, it
 * looks like a bug, and no screenshot review catches it on every screen size.
 * `QUIET_TOP` and the test that enforces it are the whole point.
 */

export const SURFACES = ['home', 'lock'];

/** What the user picked, expanded into the surfaces it touches. */
export const CHOICES = {
    full: ['home', 'lock'],
    home: ['home'],
    lock: ['lock'],
};

/**
 * Fraction of the screen height that must stay quiet, per surface.
 * Lock: the clock, date and notification stack. Home: the status bar and the
 * first row of icons.
 */
export const QUIET_TOP = { lock: 0.46, home: 0.20 };

/** Nothing bigger than this on the long edge: a 1440x3200 phone at DPR 3
 *  would otherwise ask for a 4320x9600 bitmap, which is a quarter of a
 *  gigabyte of pixels and an out-of-memory crash on a mid-range device. */
export const MAX_EDGE = 2560;

/** How much of the radial glow reads as "bright" rather than as a fade. Used
 *  both to place the glow and to check that its core clears the quiet band. */
export const GLOW_CORE = 0.35;

export const PALETTES = {
    forest: {
        label: 'Forest',
        sky: ['#1F382A', '#121614'],
        glow: 'rgba(71, 122, 95, 0.55)',
        hills: ['#254232', '#1B3227', '#15251C'],
        stem: '#477A5F',
        leaf: '#8B9D83',
        accent: '#D99C4B',
    },
    meadow: {
        label: 'Meadow',
        sky: ['#F7F5F0', '#D9DCCD'],
        glow: 'rgba(217, 156, 75, 0.35)',
        hills: ['#B9C3A8', '#93A487', '#6E8168'],
        stem: '#2C4C3B',
        leaf: '#3A4B3C',
        accent: '#C05746',
    },
    dusk: {
        label: 'Dusk',
        sky: ['#3A3350', '#1B261C'],
        glow: 'rgba(237, 178, 99, 0.45)',
        hills: ['#2E3B41', '#222E2C', '#161F1B'],
        stem: '#5C705F',
        leaf: '#A1B598',
        accent: '#EDB263',
    },
};

export const VARIANTS = Object.keys(PALETTES);

export function surfacesFor(choice) {
    return CHOICES[choice] ? CHOICES[choice].slice() : [];
}

export function paletteFor(variant) {
    return PALETTES[variant] || PALETTES.forest;
}

/**
 * Pixel size to draw at: the screen, times its pixel ratio, capped.
 * Aspect ratio is preserved -- a wallpaper stretched to the wrong shape is
 * the most visible possible failure.
 */
export function canvasSize({ width, height, ratio = 1 }) {
    const w = Math.max(1, Math.round(Number(width) || 0));
    const h = Math.max(1, Math.round(Number(height) || 0));
    const dpr = Math.max(1, Math.min(4, Number(ratio) || 1));
    const scale = Math.min(dpr, MAX_EDGE / Math.max(w, h));
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
    };
}

/** A tiny deterministic generator: the same phone must get the same wallpaper
 *  every time, or the preview shows one thing and the phone gets another. */
function rng(seed) {
    let state = seed >>> 0 || 1;
    return () => {
        state ^= state << 13; state >>>= 0;
        state ^= state >> 17;
        state ^= state << 5; state >>>= 0;
        return state / 4294967296;
    };
}

function seedFrom(width, height, surface, variant) {
    const text = `${width}x${height}:${surface}:${variant}`;
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

/**
 * Where everything goes. Plain data, no canvas, no randomness the caller
 * cannot reproduce.
 */
export function composition({ width, height, surface = 'home', variant = 'forest' }) {
    const palette = paletteFor(variant);
    const quiet = (QUIET_TOP[surface] ?? QUIET_TOP.home) * height;
    const next = rng(seedFrom(width, height, surface, variant));

    // The hills start below the quiet band and step down towards the bottom,
    // back one first so the nearer ones overlap it.
    const hills = palette.hills.map((color, index) => {
        const span = height - quiet;
        return {
            color,
            baseline: quiet + (span * (0.25 + index * 0.22)),
            amplitude: height * (0.035 - index * 0.006),
            phase: next() * Math.PI * 2,
            waves: 1 + index,
        };
    });

    // Sprigs sit on the nearest hill, so they can never intrude on the quiet
    // band no matter how tall they grow.
    const ground = hills[hills.length - 1].baseline;
    const maxStem = Math.min(height * 0.16, (ground - quiet) * 0.8);
    const count = Math.max(3, Math.min(7, Math.round(width / 260)));
    const sprigs = [];
    for (let i = 0; i < count; i += 1) {
        const stem = maxStem * (0.55 + next() * 0.45);
        sprigs.push({
            x: Math.round(width * ((i + 0.5 + (next() - 0.5) * 0.4) / count)),
            base: Math.round(ground + height * 0.01 * next()),
            stem: Math.round(stem),
            tilt: (next() - 0.5) * 0.5,
            leaves: 3 + Math.round(next() * 3),
            accent: i === Math.floor(count / 2),
        });
    }

    // The glow is sized from the space it is allowed to occupy, not chosen and
    // then hoped about. Its bright core has to clear the quiet band, and on a
    // short wide screen -- a tablet, a folded phone -- a radius picked from the
    // long edge does not. Deriving the radius from the distance actually
    // available makes the rule hold by construction rather than by luck, which
    // is why `highestInk` can assert it on every screen shape.
    const glowY = Math.round(quiet + (height - quiet) * 0.35);
    const glow = {
        x: Math.round(width * 0.5),
        y: glowY,
        // Floor, not round: the radius is derived from an upper bound, so
        // rounding up can break the very constraint it was computed from --
        // by less than a pixel, which is exactly the kind of margin that
        // makes a rule hold on most screen sizes and quietly fail on one.
        radius: Math.floor(Math.min(
            Math.max(width, height) * 0.45,
            (glowY - quiet) / GLOW_CORE,
        )),
        color: palette.glow,
    };

    return {
        width,
        height,
        surface,
        variant,
        palette,
        quietTop: quiet,
        sky: { from: palette.sky[0], to: palette.sky[1] },
        glow,
        hills,
        sprigs,
    };
}

/** The topmost pixel any decoration reaches. Exists so the quiet-band rule
 *  can be checked as arithmetic rather than by looking at a picture. */
export function highestInk(comp) {
    const hillTop = Math.min(...comp.hills.map((h) => h.baseline - h.amplitude));
    const sprigTop = Math.min(...comp.sprigs.map((s) => s.base - s.stem));
    return Math.min(hillTop, sprigTop, comp.glow.y - comp.glow.radius * GLOW_CORE);
}

export function paint(ctx, comp) {
    const { width, height, palette } = comp;

    const sky = ctx.createLinearGradient(0, 0, 0, height);
    sky.addColorStop(0, comp.sky.from);
    sky.addColorStop(1, comp.sky.to);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, width, height);

    const glow = ctx.createRadialGradient(
        comp.glow.x, comp.glow.y, 0, comp.glow.x, comp.glow.y, comp.glow.radius);
    glow.addColorStop(0, comp.glow.color);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    comp.hills.forEach((hill) => {
        ctx.beginPath();
        ctx.moveTo(0, height);
        for (let x = 0; x <= width; x += Math.max(2, width / 180)) {
            const t = (x / width) * Math.PI * 2 * hill.waves + hill.phase;
            ctx.lineTo(x, hill.baseline + Math.sin(t) * hill.amplitude);
        }
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fillStyle = hill.color;
        ctx.fill();
    });

    comp.sprigs.forEach((sprig) => {
        ctx.save();
        ctx.translate(sprig.x, sprig.base);
        ctx.rotate(sprig.tilt);
        ctx.strokeStyle = palette.stem;
        ctx.lineWidth = Math.max(1.5, comp.width / 600);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -sprig.stem);
        ctx.stroke();

        ctx.fillStyle = sprig.accent ? palette.accent : palette.leaf;
        for (let i = 0; i < sprig.leaves; i += 1) {
            const at = -sprig.stem * (0.35 + (i / sprig.leaves) * 0.6);
            const size = sprig.stem * 0.22 * (1 - i / (sprig.leaves + 2));
            const side = i % 2 === 0 ? 1 : -1;
            ctx.beginPath();
            ctx.ellipse(side * size * 0.9, at, size, size * 0.42,
                side * -0.6, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    });
}

/**
 * The finished wallpaper as a PNG data URL.
 *
 * PNG rather than JPEG on purpose: this is flat colour and smooth gradients,
 * exactly the content JPEG bands badly on, and it goes straight to the system
 * wallpaper where the banding would be permanent.
 */
export function renderWallpaper({ width, height, ratio = 1, surface = 'home', variant = 'forest' }) {
    const size = canvasSize({ width, height, ratio });
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    paint(ctx, composition({ ...size, surface, variant }));
    return canvas.toDataURL('image/png');
}
